import { useEffect, useState, useRef } from 'react';

import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ConfirmModal,
  FormActions,
  Modal,
  ModalActions,
  StatusStrip,
  type StatusTone,
} from '~/components/ui';
import { Select, FormField, TextField, ToggleField } from '~/components/form';
import {
  DomainCard,
  type HarnessProgress,
  type StepStatus,
} from '~/components/pages/domain/components/domainCard';
import {
  DOMAIN_HEADER,
  DOMAIN_OPTIONS,
  SITE_PLANS,
  type SitePlan,
} from '~/components/Constants/domainConstants';
import {
  useGetDomainProgressQuery,
  useLazyGetSiteTypeQuery,
  useStartDomainSetupMutation,
} from '~/modules/query/domain';
import { useConfigureDnsMutation } from '~/modules/query/utilities';
import { useFormValidation } from '~/hooks/useFormValidation';
import { domainSchema } from '~/lib/domainForm';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';

const DNS_STEP = 'Customer DNS records';

const FORM_ID = 'domain-form';
const INITIAL_VALUES: DomainFormValues = { domain: '', baySelect: '', raze: false };

interface DomainFormValues {
  domain: string;
  baySelect: string;
  raze: boolean;
}

/**
 * One form for a new domain. Per site type (SITE_PLANS) it starts the service's domain-setup
 * job (our alias, certs, and for Office the whole zone) and/or writes the customer's DNS
 * records through /dns/config — after the job succeeds when it runs both.
 */
export const DomainMainContent = () => {
  const [openModal, setOpenModal] = useState(false);
  const [razeRequest, setRazeRequest] = useState<{ domain: string; plan: SitePlan } | null>(null);
  const [activeSetup, setActiveSetup] = useState<{ domain: string; plan: SitePlan } | null>(null);
  const [skeleton, setSkeleton] = useState<HarnessProgress>({});
  const [polling, setPolling] = useState(true);
  const [dnsStatus, setDnsStatus] = useState<StepStatus | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [values, setValues] = useState<DomainFormValues>(INITIAL_VALUES);
  const validation = useFormValidation(domainSchema, values);
  const missesRef = useRef(0);
  const dnsStartedRef = useRef(false);

  const [configureDns] = useConfigureDnsMutation();
  const [getSiteType] = useLazyGetSiteTypeQuery();
  const [startDomainSetup] = useStartDomainSetupMutation();

  // The job is polled every 5s while the modal is open; `polling` stops it once the job ends or is lost.
  const { data: jobProgress, error: progressError } = useGetDomainProgressQuery(
    { siteType: activeSetup?.plan.job ?? '', domain: activeSetup?.domain ?? '' },
    { pollingInterval: 5000, skip: !activeSetup || !polling, refetchOnMountOrArgChange: true },
  );

  const runDns = async (domain: string, dnsType: string) => {
    setDnsStatus({});
    try {
      await configureDns({ domainName: domain, siteType: dnsType }).unwrap();
      setDnsStatus({ isOk: true });
    } catch (err) {
      console.error(err);
      setDnsStatus({
        result: {
          isErr: true,
          message:
            apiErrorMessage(err as ApiErrorInput) ?? 'The DNS could not be configured. Try again.',
        },
      });
    }
  };

  // A finished job writes the customer's DNS records when the site type has them.
  useEffect(() => {
    if (!activeSetup || !jobProgress) return;
    const { domain, plan } = activeSetup;
    const hasFailed = Object.values(jobProgress.statusesOfSteps ?? {}).some(
      (step) => step?.result?.isErr && !step?.isSkipped,
    );
    const finished =
      jobProgress.isOk || (jobProgress.isDone && !jobProgress.isRunning) || hasFailed;
    if (!finished) return;
    setPolling(false);
    if (!plan.dns || dnsStartedRef.current) return;
    dnsStartedRef.current = true;
    if (jobProgress.isOk) void runDns(domain, plan.dns);
    else
      setDnsStatus({
        isSkipped: true,
        result: { message: 'Not run: the setup job did not finish successfully.' },
      });
  }, [activeSetup, jobProgress]);

  // Six straight misses (30s): stop polling and release the modal rather than hold it open forever.
  useEffect(() => {
    if (!progressError) {
      missesRef.current = 0;
      return;
    }
    if (++missesRef.current < 6) return;
    setPolling(false);
    if (activeSetup?.plan.dns)
      setDnsStatus({
        isSkipped: true,
        result: {
          message:
            'Not run: lost track of the setup job. Check its progress, then write the DNS records from Upsert CNAME or rerun this setup.',
        },
      });
  }, [progressError, activeSetup]);

  useEffect(() => {
    if (!openModal) {
      setActiveSetup(null);
      setSkeleton({});
      setPolling(true);
      missesRef.current = 0;
      dnsStartedRef.current = false;
      setDnsStatus(null);
      setSetupError(null);
    }
  }, [openModal]);

  const createDomain = async (domain: string, plan: SitePlan, raze: boolean) => {
    if (plan.dns) setDnsStatus({});
    if (!plan.job) {
      void runDns(domain, plan.dns as string);
      return;
    }
    try {
      const fetchedSkeleton = await getSiteType(plan.job)
        .unwrap()
        .catch(() => null);
      if (fetchedSkeleton) setSkeleton(fetchedSkeleton);

      const res = await startDomainSetup({ domain, siteType: plan.job, raze }).unwrap();
      if (res.status === 0) {
        missesRef.current = 0;
        dnsStartedRef.current = false;
        setPolling(true);
        setActiveSetup({ domain, plan });
      } else {
        setSetupError(
          apiErrorMessage(res as ApiErrorInput) || 'Domain setup could not be started. Try again.',
        );
      }
    } catch (err) {
      console.error(err);
      setSetupError(
        apiErrorMessage(err as ApiErrorInput) || 'Domain setup could not be started. Try again.',
      );
    }
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validation.submit()) return;
    const plan = SITE_PLANS[values.baySelect];
    if (!plan) return;
    const domain = values.domain.trim();
    const raze = Boolean(plan.job && values.raze);
    if (raze) {
      setRazeRequest({ domain, plan });
      return;
    }
    startSetup(domain, plan, false);
  };

  const startSetup = (domain: string, plan: SitePlan, raze: boolean) => {
    setOpenModal(true);
    createDomain(domain, plan, raze);
  };

  // The DNS step runs from this component after the job, so the modal can't close until it lands.
  const dnsPending =
    dnsStatus !== null &&
    !dnsStatus.isOk &&
    !dnsStatus.result &&
    !dnsStatus.isSkipped &&
    !setupError;

  // The job's own steps, then the customer DNS step when this site type writes one.
  const shownProgress = jobProgress ?? skeleton;
  const progress: HarnessProgress = dnsStatus
    ? {
        ...shownProgress,
        statusesOfSteps: { ...shownProgress.statusesOfSteps, [DNS_STEP]: dnsStatus },
      }
    : shownProgress;

  // One strip above the steps: Failed once any step fails, Done once every step is ok or skipped.
  const steps = Object.values(progress.statusesOfSteps ?? {});
  const stepsDone = steps.filter((step) => step?.isOk || step?.isSkipped).length;
  const runChip: { label: string; tone: StatusTone } = steps.some(
    (step) => step?.result?.isErr && !step?.isSkipped,
  )
    ? { label: 'Failed', tone: 'error' }
    : steps.length > 0 && stepsDone === steps.length
      ? { label: 'Done', tone: 'success' }
      : { label: 'Running', tone: 'brand' };

  const plan = SITE_PLANS[values.baySelect];

  return (
    <Card>
      <CardHeader title="Set up a domain" description={DOMAIN_HEADER} />
      <form id={FORM_ID} noValidate onSubmit={onSubmit}>
        <CardBody className="space-y-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <TextField
              id="domain"
              name="domain"
              testId="domain-name-input"
              label="Domain"
              required
              placeholder="example.com"
              value={values.domain}
              onChange={(value) => setValues((current) => ({ ...current, domain: value }))}
              onBlur={() => validation.touch('domain')}
              error={validation.errors.domain}
            />

            <FormField
              id="baySelect"
              label="Site type"
              required
              helperText={plan?.summary}
              error={validation.errors.baySelect}
            >
              <Select
                id="baySelect"
                testId="domain-site-type-select"
                options={DOMAIN_OPTIONS}
                placeholder="Site type"
                placeholderSelectable
                value={values.baySelect}
                onChange={(value) => {
                  setValues((current) => ({ ...current, baySelect: value }));
                  validation.touch('baySelect');
                }}
              />
            </FormField>
          </div>

          {plan?.job && (
            <ToggleField
              id="domain-raze"
              testId="domain-raze-toggle"
              label="Replace existing setup"
              text="Delete the existing domain setup first"
              helperText="Permanently removes any current DNS records and certificates for this domain, then recreates them. Leave off to keep the existing setup."
              checked={values.raze}
              onChange={(checked) => setValues((current) => ({ ...current, raze: checked }))}
            />
          )}
        </CardBody>
        <CardFooter>
          <FormActions
            form={FORM_ID}
            saveLabel="Set up domain"
            saveTestId="domain-create-button"
            disabled={values.domain === '' || !plan}
            disabledReason="Enter a domain and choose a site type"
          />
        </CardFooter>
      </form>

      <ConfirmModal
        isOpen={razeRequest !== null}
        variant="danger"
        title="Replace the existing setup?"
        message={`RAZE will permanently delete the DNS records and certificates for '${razeRequest?.domain}', then recreate them.`}
        dangerNote="The domain's current DNS records and certificates cannot be recovered."
        confirmText={razeRequest?.domain}
        confirmLabel="Delete and set up"
        testId="domain-raze-confirm"
        onClose={() => setRazeRequest(null)}
        onConfirm={() => {
          if (razeRequest) startSetup(razeRequest.domain, razeRequest.plan, true);
          setRazeRequest(null);
        }}
      />

      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        busy={dnsPending}
        title="Domain setup progress"
        description={
          dnsPending
            ? 'Keep this open: the customer DNS records are written once the setup job finishes.'
            : undefined
        }
        testId="domain-progress-modal"
        footer={<ModalActions onDismiss={() => setOpenModal(false)} dismissDisabled={dnsPending} />}
      >
        {setupError ? (
          <Alert variant="error">
            <p className="text-sm text-gray-800 dark:text-white/90">{setupError}</p>
          </Alert>
        ) : (
          <div className="space-y-4">
            {activeSetup && (
              <StatusStrip
                chip={runChip}
                id={activeSetup.domain}
                facts={[`${stepsDone} of ${steps.length} steps done`]}
                testId="domain-run-status"
              />
            )}
            <DomainCard displayLoop={progress} />
          </div>
        )}
      </Modal>
    </Card>
  );
};
